import { type ClassValue, clsx } from "clsx";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

import { DomainMap, getBadgeProps } from "../constants/LabelPalette";
import type { Deserializable } from "../models/deserializable";

import { EVENTS } from "@/common/constants/Calendar";
import type { EventType } from "@/common/types/Calendar";
import {
  dateIsAfter,
  dateIsBefore,
  dateIsBetween,
  dateIsSame,
  endOfDate,
  splitDatePeriodByWeek,
} from "@/common/utils/date";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function generateBoundary() {
  const length = 16; // Adjust the length as per your requirements
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let boundary = "";

  for (let i = 0; i < length; i++) {
    boundary += characters.charAt(
      Math.floor(Math.random() * characters.length),
    );
  }

  return boundary;
}

export function sorteEventsOrTasks(eventsOrTasks: EventType[]): EventType[] {
  return eventsOrTasks.sort((a, b) => {
    if (dateIsSame(a.start, b.start, "milliseconds")) return 0;
    if (dateIsBefore(a.start, b.start, "milliseconds")) return -1;
    return 1;
  });
}

export function splitEventsOrTasks(eventOrTasks: EventType[]): EventType[] {
  const newEventsOrTasks: EventType[] = [];

  eventOrTasks.forEach((item) => {
    const endWeek = endOfDate(item.start, "week");

    if (
      item.end &&
      dateIsAfter(item.end, item.start, "date") &&
      endWeek &&
      dateIsBefore(endWeek, item.end, "date")
    ) {
      const splitEvents = splitDatePeriodByWeek({
        start: item.start,
        end: item.end,
      }).map((period, index) => ({
        ...item,
        ...period,
        id: `${item.id}-${index + 1}`,
        parentId: item.id,
      }));

      newEventsOrTasks.push(...splitEvents);
    } else {
      newEventsOrTasks.push(item);
    }
  });

  return newEventsOrTasks;
}

interface EventCheck {
  simpleTaskOrEvent: boolean;
  allDaysTaskOrEvent: boolean;
  multipleDaysEvent: {
    notAllDay: boolean;
    allDay: boolean;
    anything: boolean;
  };
}

export function checkEvent(eventOrTasks: EventType): EventCheck {
  const checkStartAndEndIsSameDate =
    !!eventOrTasks.end &&
    dateIsSame(eventOrTasks.end, eventOrTasks.start, "date");
  const allMultipleDayEvent =
    !!eventOrTasks.end &&
    dateIsAfter(eventOrTasks.end, eventOrTasks.start, "date");

  return {
    simpleTaskOrEvent:
      !eventOrTasks.allDay && (!eventOrTasks.end || checkStartAndEndIsSameDate),
    allDaysTaskOrEvent: eventOrTasks.allDay && !eventOrTasks.end,
    multipleDaysEvent: {
      notAllDay: !eventOrTasks.allDay && allMultipleDayEvent,
      allDay: eventOrTasks.allDay && allMultipleDayEvent,
      anything: allMultipleDayEvent,
    },
  };
}

export function checkEventTakePlaceOnDay(
  event: EventType,
  date: Date,
): boolean {
  if (!event.end) {
    return dateIsSame(event.start, date, "date");
  }

  return dateIsBetween(date, event.start, event.end, "date", {
    start: true,
    end: true,
  });
}

export function fillMultipleDayEventByStartDayEvent(
  eventsOrTasks: EventType[],
  allEvents: EventType[] = EVENTS,
): EventType[] {
  return eventsOrTasks.map((item) => {
    if (checkEvent(item).multipleDaysEvent.anything) {
      const eventStart = item.start;
      const events = sorteEventsOrTasks(
        allEvents.filter((event) =>
          checkEventTakePlaceOnDay(event, eventStart),
        ),
      );

      if (events.length > 1) {
        return {
          ...item,
          startDayEvents: events,
        };
      }
    }
    return item;
  });
}

export function isPathActive(
  menuItemPath: string,
  currentPath: string,
  matchPattern?: string,
): boolean {
  // Use explicit regex pattern when provided (handles dynamic-segment URLs)
  if (matchPattern) {
    return new RegExp(matchPattern).test(currentPath);
  }

  const normalizedMenuPath = menuItemPath.replace(/\/+$/, ""); // remove trailing slashes
  const normalizedCurrentPath = currentPath.replace(/\/+$/, "");

  // Special case: only mark "/" as active if current path is exactly "/"
  if (normalizedMenuPath === "") return normalizedCurrentPath === "";

  return (
    normalizedCurrentPath === normalizedMenuPath ||
    normalizedCurrentPath.startsWith(normalizedMenuPath + "/")
  );
}

export const getCSSVar = (name: string) => {
  if (typeof window === "undefined") return "";
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  if (!value) return "";
  if (
    value.startsWith("#") ||
    value.startsWith("rgb") ||
    value.startsWith("hsl") ||
    value.startsWith("rgba") ||
    value.startsWith("hsla")
  ) {
    return value;
  }
  return `hsl(${value})`;
};

export const getApiUrl = (
  base: string,
  pathParam?: string | number,
  query?: Record<string, string | number>,
): string => {
  let url = base;
  if (pathParam !== undefined && pathParam !== null) {
    url += encodeURIComponent(pathParam);
  }
  if (query && Object.keys(query).length > 0) {
    const queryString = new URLSearchParams(
      query as Record<string, string>,
    ).toString();
    url += `?${queryString}`;
  }
  return url;
};

export const formatScheduleTime = (
  timestamp: number | string | Date,
  format: string = "MMM D, YYYY h:mm A",
): string => {
  if (!timestamp) return "";
  return dayjs(timestamp).format(format);
};

export const replaceTestStepById = (testSteps, id, newStep) => {
  return testSteps.map((step) => {
    // If the current step matches the ID, replace the whole object
    if (step.id === id) {
      return {
        ...newStep, // Replace with the new step object
        id: step.id, // Maintain the original ID
      };
    }

    // If the current step has childSteps, recursively update them
    if (step.childSteps && step.childSteps.length > 0) {
      return {
        ...step,
        childSteps: replaceTestStepById(step.childSteps, id, newStep), // Recursively handle childSteps
      };
    }

    // If no match, return the step as is
    return step;
  });
};

// Generic function to find an object in an array by a specified key and value
export function findDataByKey(array, key, keyValue) {
  // Find the matching object based on the provided key and value
  const returnData =
    (array && array.find((item) => item[key] === keyValue)) || null;
  return returnData;
}

export function replaceNaturalTextObj(templateData, dataMap) {
  let naturalText = templateData?.grammar;

  // Replace placeholders of the format ${key}, #{key}, and @{key}
  const patterns = [
    { regex: /\${(.*?)}/g, format: "test" }, // Matches ${key}
    { regex: /#{(.*?)}/g, format: "uiIdentifier" }, // Matches #{key}
    { regex: /@{(.*?)}/g, format: "attribute" }, // Matches @{key}
  ];

  // Loop through each pattern and replace the corresponding values
  patterns.forEach(({ regex, format: _format }) => {
    naturalText = naturalText?.replace(regex, (match, key) => {
      return dataMap[key] || match; // Replace with value or keep the original if undefined
    });
  });

  return naturalText; // Return the updated string
}

export const flattenTestSteps = (steps: any[]): any[] => {
  const flattened: any[] = [];
  const visited = new Set<number>();

  const traverse = (currentSteps: any[] | null) => {
    if (!Array.isArray(currentSteps)) return;

    for (const step of currentSteps) {
      if (!step || typeof step !== "object") continue;
      if (visited.has(step.id)) continue;

      visited.add(step.id);
      flattened.push(step);

      if (Array.isArray(step.childSteps)) {
        traverse(step.childSteps);
      }
    }
  };

  traverse(steps);
  return flattened;
};

export function deserializeList<T extends Deserializable>(
  cls: new () => T,
  json: any[],
): T[] {
  return json.map((item) => new cls().deserialize(item));
}

export function deserializeOne<T extends Deserializable>(
  cls: new () => T,
  json: any,
): T {
  return new cls().deserialize(json);
}

export function filterByFuzzyKeywords<T>(
  items: T[],
  searchTerm: string,
  keys: (keyof T)[],
): T[] {
  const trimmed = searchTerm.trim().toLowerCase();
  if (!trimmed) return items;

  const inputWords = trimmed.split(/\s+/);

  return items.filter((item) => {
    return keys.some((key) => {
      const value = String(item[key] ?? "").toLowerCase();
      return inputWords.every((word) => value.includes(word));
    });
  });
}

// Helper function to replace test data values with specific format
export const replaceAddonTestData = (type, value) => {
  switch (type) {
    case "runtime":
      return `$|${value}|`;
    case "global":
      return `*|${value}|`;
    case "parameter":
      return `@|${value}|`;
    case "function":
      return `!|${value}|`;
    default:
      return value;
  }
};

export const formatDuration = (ms: number) => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const centiseconds = Math.floor((ms % 1000) / 10); // 1 cs = 10ms

  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  const cc = String(centiseconds).padStart(2, "0");

  return `${mm}:${ss}:${cc}`;
};

export const formatDurationInText = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const milliseconds = ms % 1000;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];

  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
  if (seconds > 0) parts.push(`${seconds} second${seconds !== 1 ? "s" : ""}`);
  if (milliseconds > 0)
    parts.push(`${milliseconds} millisecond${milliseconds !== 1 ? "s" : ""}`);

  if (parts.length === 0) return "0 milliseconds";

  return parts.join(" ");
};

export function getNameInitial(
  firstName?: string | null,
  lastName?: string | null,
): string {
  if (!firstName && !lastName) return "?";

  const f = firstName?.trim().charAt(0).toUpperCase() ?? "";
  const l = lastName?.trim().charAt(0).toUpperCase() ?? "";

  return (f + l).trim() || "?";
}

export const findPathTitlesToNode = (
  nodes: any[],
  targetId: number,
  path: string[] = [],
): string[] | null => {
  for (const node of nodes) {
    const newPath = [...path, node.title];

    if (node.id === targetId) {
      return newPath;
    }

    if (node.children && node.children.length > 0) {
      const result = findPathTitlesToNode(node.children, targetId, newPath);
      if (result) return result;
    }
  }

  return null;
};

// Helper to convert headers array to object
export function headersArrayToObject(headersArr) {
  if (!Array.isArray(headersArr)) return {};
  return headersArr.reduce((acc, h) => {
    if (h.name && h.value !== undefined) acc[h.name] = h.value;
    return acc;
  }, {});
}

// Helper functions
export function getPlatformFromOsType(osType: string): string {
  if (!osType) return "Unknown";
  if (osType.includes("WIN")) return "Windows";
  if (osType.includes("MAC")) return "Mac";
  if (osType.includes("LINUX")) return "Linux";
  return osType;
}

export function getPlatformFromMobileOStype(osName: string): string {
  if (!osName) return "Unknown";
  if (osName.includes("iOS")) return "iOS";
  if (osName.includes("Android")) return "Android";
  return osName;
}

export const platformIconUrlMap: Record<string, string> = {
  WINDOWS:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/windows.svg",
  MAC: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/apple.svg",
  IOS: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/apple.svg",
  LINUX:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/linux.svg",
  ANDROID:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/android.svg",
};

export const browserIconUrlMap: Record<string, string> = {
  CHROME:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googlechrome.svg",
  FIREFOX:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/firefoxbrowser.svg",
  SAFARI:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/safari.svg",
  EDGE: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/microsoftedge.svg",
};

export const capitalize = (str?: string | null): string => {
  if (!str || str.trim() === "") return "-";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export function generateDryRunName(): string {
  const now = new Date();

  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");

  const hh = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  const ms = String(now.getMilliseconds()).padStart(3, "0");

  return `Dry run ${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}.${ms}`;
}

// Convert object to query string like ?key1=value1&key2=value2...
export const buildQueryString = (params: Record<string, any>) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => query.append(`${key}[]`, String(v)));
    } else if (value !== undefined && value !== null) {
      query.append(key, String(value));
    }
  });
  return query.toString();
};

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Utility to convert file to base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export function humanizeKey(raw?: string | null): string {
  if (!raw) return "";
  // Normalize separators to spaces
  let s = String(raw).trim();

  // convert camelCase boundary to space (e.g. inProgress -> in Progress)
  s = s.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

  // replace underscores and hyphens with spaces
  s = s.replace(/[_-]+/g, " ");

  // collapse multiple spaces
  s = s.replace(/\s+/g, " ").trim();

  // Title case each word
  const smallWords = new Set(["and", "or", "of", "the", "a", "an", "to", "in"]);
  return s
    .toLowerCase()
    .split(" ")
    .map((w) =>
      smallWords.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ");
}

export function formatDomainValue(
  domainMap?: DomainMap | null,
  value?: string | null,
): string {
  if (!value) return "";
  if (domainMap) {
    try {
      const p = getBadgeProps(domainMap, value, "unknown");
      if (p && p.text) return p.text;
    } catch {
      // fallback to humanize
    }
  }
  return humanizeKey(value);
}

export function formatLabel(raw: string, forceRaw = false): string {
  if (forceRaw) return raw; // allow bypass
  return raw
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
